import { history as QuotePageHistory } from "QuotePage/Store";
import { take, select, fork, call } from "redux-saga/effects";
import { history } from "ZeroRuby/App";

import { STEP_COMPLETED } from "./actions";
import { getEmployeeApplication, getFirstIncompleteURL, getAdminMode, getAvailablePlans, getHealthAccountBenefitStatus } from "./api";
import { getEmployeeHashId } from "./selectors";
import {
  familyInformationURL,
  chooseMedicalPlanURL,
  chooseDentalPlanURL,
  chooseVisionPlanURL,
  chooseAccidentPlanURL,
  chooseLifePlanURL,
  chooseTermLifePlanURL,
  chooseWholeLifePlanURL,
  chooseShortTermDisabilityPlanURL,
  chooseLongTermDisabilityPlanURL,
  healthAccountInformationURL,
  additionalInformationURL,
  signURL,
  doneURL,
  isRenewal,
  inNewEnrollmentFlow,
} from "./urls";

import type { StepCompletedAction, StepName } from "./actions";
import type { EmployeePolicy } from "Utils/types";

const eeMissingAdditionalInformation = (employeePolicy: EmployeePolicy | null | undefined): boolean => {
  return employeePolicy.employeeForm && (!employeePolicy.employeeForm.jobTitle || !employeePolicy.employeeForm.hiredAt);
};

const nextUrl = (
  step: StepName,
  availablePlans: Array<"medical" | "dental" | "vision">,
  employeePolicy: EmployeePolicy | null | undefined,
  adminMode: boolean,
  employeeHashId: string,
  healthAccountBenefitStatus: string,
): string => {
  const steps = [
    { name: "basicInformation", url: null },
    { name: "familyInformation", url: familyInformationURL(adminMode, isRenewal(), employeeHashId) },
    availablePlans.includes("medical") ? { name: "chooseMedicalPlan", url: chooseMedicalPlanURL(adminMode, isRenewal(), employeeHashId) } : null,
    availablePlans.includes("dental") ? { name: "chooseDentalPlan", url: chooseDentalPlanURL(adminMode, isRenewal(), employeeHashId) } : null,
    availablePlans.includes("vision") ? { name: "chooseVisionPlan", url: chooseVisionPlanURL(adminMode, isRenewal(), employeeHashId) } : null,
    availablePlans.includes("accident") ? { name: "chooseAccidentPlan", url: chooseAccidentPlanURL(adminMode, isRenewal(), employeeHashId) } : null,
    availablePlans.includes("life") ? { name: "chooseLifePlan", url: chooseLifePlanURL(adminMode, isRenewal(), employeeHashId) } : null,
    availablePlans.includes("term_life") ? { name: "chooseTermLifePlan", url: chooseTermLifePlanURL(adminMode, isRenewal(), employeeHashId) } : null,
    availablePlans.includes("whole_life") ? { name: "chooseWholeLifePlan", url: chooseWholeLifePlanURL(adminMode, isRenewal(), employeeHashId) } : null,
    availablePlans.includes("short_term_disability")
      ? { name: "chooseShortTermDisabilityPlan", url: chooseShortTermDisabilityPlanURL(adminMode, isRenewal(), employeeHashId) }
      : null,
    availablePlans.includes("long_term_disability")
      ? { name: "chooseLongTermDisabilityPlan", url: chooseLongTermDisabilityPlanURL(adminMode, isRenewal(), employeeHashId) }
      : null,
    availablePlans.includes("shortTermDisability")
      ? { name: "chooseShortTermDisabilityPlan", url: chooseShortTermDisabilityPlanURL(adminMode, isRenewal(), employeeHashId) }
      : null,
    ["done", "incomplete"].includes(healthAccountBenefitStatus)
      ? { name: "healthAccountInformation", url: healthAccountInformationURL(adminMode, isRenewal(), employeeHashId) }
      : null,
    { name: "additionalInformation", url: additionalInformationURL(adminMode, isRenewal(), employeeHashId) },
    { name: "sign", url: signURL(adminMode, isRenewal(), employeeHashId) },
    { name: "done", url: doneURL(adminMode, isRenewal(), employeeHashId) },
  ].filter((step) => step !== null);
  const completedStepIndex = steps.findIndex((s) => s.name === step);
  if (completedStepIndex === -1) {
    throw new Error(`Unknown step: ${JSON.stringify(step)}`);
  }
  const nextStep = steps[Math.min(completedStepIndex + 1, steps.length - 1)];
  return nextStep.url;
};

export default function* navigationSaga(): Generator<any, any, any> {
  // Listen for the initial load of the employee policy to do the initial
  // redirect.
  yield fork(function* () {
    yield take("employeeApplicationApi/executeQuery/fulfilled");
    const url = yield select(getFirstIncompleteURL);
    if (inNewEnrollmentFlow()) {
      yield call(QuotePageHistory.push, url);
    } else {
      yield call(history.push, url);
    }
  });

  while (true) {
    const action: StepCompletedAction = yield take(STEP_COMPLETED);
    const availablePlans = yield select(getAvailablePlans);
    const employeePolicy = yield select(getEmployeeApplication);
    const adminMode = yield select(getAdminMode);
    const employeeHashId = yield select(getEmployeeHashId);
    const healthAccountBenefitStatus = yield select(getHealthAccountBenefitStatus);
    const url = nextUrl(action.payload, availablePlans, employeePolicy, adminMode, employeeHashId, healthAccountBenefitStatus);

    if (inNewEnrollmentFlow()) {
      yield call(QuotePageHistory.push, url);
    } else {
      yield call(history.push, url);
    }
  }
}
